import { useEffect } from "react";
import { useRouteError, isRouteErrorResponse, useNavigate } from "react-router";
import * as Sentry from "@sentry/react";

/**
 * Route-level error boundary for React Router.
 *
 * Usage: export from any route module as `ErrorBoundary`, or set as the
 * `ErrorBoundary` property in the route config.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();

  useEffect(() => {
    if (error instanceof Error) {
      Sentry.captureException(error);
    } else if (isRouteErrorResponse(error) && error.status >= 500) {
      Sentry.captureException(new Error(`Route error ${error.status}: ${error.statusText}`));
    }
  }, [error]);

  let title = "Something went wrong";
  let message = "An unexpected error occurred while loading this page.";

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      title = "Page not found";
      message = "The page you're looking for doesn't exist.";
    } else {
      title = `Error ${error.status}`;
      message = error.statusText || message;
    }
  } else if (error instanceof Error) {
    message = error.message;
  }

  return (
    <div className="flex flex-col items-center justify-center px-5 py-24">
      <div className="w-full max-w-md text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full border border-status-danger-border bg-status-danger-bg text-status-danger-text">
          <svg
            className="h-5 w-5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M10.24 4.5 2.7 17.5a2 2 0 0 0 1.73 3h15.14a2 2 0 0 0 1.73-3L13.76 4.5a2 2 0 0 0-3.52 0Z" />
            <path d="M12 9.5v4" />
            <path d="M12 17h.01" />
          </svg>
        </div>
        <h1 className="text-xl font-semibold text-text-primary">{title}</h1>
        <p className="mt-2 text-sm leading-6 text-text-secondary">{message}</p>
        <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <button
            type="button"
            onClick={() => navigate(0)}
            className="rounded-lg border border-border-default bg-surface-raised px-4 py-2 text-sm font-medium text-text-primary hover:bg-surface-hover"
          >
            Retry
          </button>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="rounded-lg bg-surface-active px-4 py-2 text-sm font-medium text-text-on-active"
          >
            Go home
          </button>
        </div>
      </div>
    </div>
  );
}
